'use client';

import heroBackground from '@/public/sales-page/hero.png';
import heroBackgroundMobile from '@/public/sales-page/hero-mobile.png';
import Image from 'next/image';
import { useRef, useState } from 'react';
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion';
import { PortableText, PortableTextBlock } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import Link from 'next/link';
import LogoIcon from '@/components/global/LogoIcon';
import { CTAButton } from '@/shadcn/ui/button';
import { GROQButton } from '@/sanity/lib/definitions';
import { IMuxVideoPlayer } from '@/sanity/globals/VideoPlayer';
import { PlayVideoCursorFollower } from '../global/PlayVideoCursorFollower';
import { VideoControlsMinimal } from '../waitlist/VideoControlsMinimal';
import MuxHLSVideo from '@/components/utils/MuxHSLVideo';
import { useIsMobile } from '@/hooks/useIsMobile';
import VideoAsset from '@/components/utils/VideoAsset';
import GlassSurface from '@/components/global/utils/GlassSurface';
import ScrollIndicator from '@/components/global/ui/ScrollIndicator';

interface IHeroProps {
  heroStickyButton: {
    heroStickyButton1: GROQButton;
    heroStickyButton2: GROQButton;
  };
  textPreTitle?: {
    firstText?: string;
    secondText?: string;
    secondTextBold?: string;
  };
  glassLabel?: {
    firstText?: string;
    secondText?: string;
  };
  label: string;
  title: PortableTextBlock[];
  useVideo?: boolean;
  backgroundVideo?: IMuxVideoPlayer;
  backgroundVideoMobile?: IMuxVideoPlayer;
}

export default function Hero({
  heroStickyButton,
  textPreTitle,
  glassLabel,
  label,
  title,
  useVideo,
  backgroundVideo,
  backgroundVideoMobile,
}: IHeroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLVideoElement | null>(null);
  const modalRef = useRef<HTMLVideoElement | null>(null);
  const isMobile = useIsMobile();

  const [open, setOpen] = useState(false);

  const playbackId =
  (isMobile
    ? backgroundVideoMobile?.muxVideo?.playbackId
    : backgroundVideo?.muxVideo?.playbackId) ??
  backgroundVideo?.muxVideo?.playbackId;

  // Track scroll progress of the section
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  // Transform scroll progress to translateY for parallax effect
  const videoY = useTransform(scrollYProgress, [0, 0.15, 1], [0, 0, 300]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.76, 0, 0.24, 1],
      },
    },
  };

  const ease = [0.76, 0, 0.24, 1];

  const openOverlay = () => {
    const mv = modalRef.current;
    if (mv) {
      try {
        mv.muted = false;
        mv.volume = 1;
        mv.removeAttribute('muted');
        mv.currentTime = 0;
      } catch {}
    }
    setOpen(true);
  };

  const closeOverlay = () => {
    const mv = modalRef.current;
    if (mv) {
      try {
        mv.pause();
        mv.muted = true;
        mv.volume = 0;
        mv.removeAttribute('controls');
        mv.currentTime = 0;
      } catch {}
    }
    setOpen(false);
  };

  return (
    <section
      ref={sectionRef}
      className="w-full flex flex-col items-center relative"
    >
      <div className="lg:sticky top-0 w-full lg:h-[8.125rem] flex items-center lg:bg-black/75 lg:backdrop-blur-[1rem] z-50">
        <div className="w-full px-[2rem] lg:px-[9rem] flex flex-col gap-[5.18rem] lg:gap-0 justify-center lg:grid grid-cols-3 items-center pt-[3rem] lg:pt-0">
          <div className="flex flex-row items-center gap-[1rem] font-nunito text-[#F2F0EF] text-[2rem] font-[200] prose-bold:font-[600] leading-[100%] px-[2rem] lg:px-0">
            {textPreTitle?.firstText && (
              <p><b>{textPreTitle.firstText}</b></p>
            )}
            <span className="w-[4rem] h-[0.125rem] bg-[#898989]"/>
            {(textPreTitle?.secondText || textPreTitle?.secondTextBold) && (
              <p>{textPreTitle?.secondText} <b>{textPreTitle?.secondTextBold}</b></p>
            )}
          </div>
          <Link className="block w-fit lg:mx-auto" href="/" aria-label="Veylor">
            <LogoIcon fill="#F2F0EF" className="w-[5.875rem] lg:w-[4.67338rem] h-auto aspect-[47/40]" />
          </Link>
          <div className="hidden lg:flex gap-[2rem] items-center justify-end">
            <CTAButton className="[&_button]:!py-[2rem] [&_button]:!px-[2.5rem]" data={heroStickyButton?.heroStickyButton1} />
            <CTAButton className="[&_button]:!min-h-0 [&_button]:!py-[2rem] [&_button]:!bg-transparent [&_button]:!backdrop-blur-0" data={heroStickyButton?.heroStickyButton2} />
          </div>
        </div>
      </div>

      <div className="relative flex lg:justify-between justify-end flex-col min-h-[85.5rem] lg:min-h-[126.75rem] w-full overflow-clip px-0 lg:px-[9rem] pb-[3rem] lg:pb-[18rem] lg:items-start -mt-[8.125rem]">
        <motion.div
          className="lg:mt-auto w-full relative z-10 flex flex-col"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={containerVariants}
        >
          <motion.div
            className="flex flex-col gap-[4rem] lg:w-[106.25rem] mx-auto lg:mx-0"
            variants={itemVariants}
          >
            <GlassSurface
              width="max-content"
              height="auto"
              borderRadius={6}
              backgroundOpacity={0}
              opacity={0.93}
              blur={10}
              displace={5}
              className="w-fit"
            >
              <div className="flex flex-row items-center gap-[1.62rem] p-[2rem]">
                <svg className="w-[3.7995rem] h-[2.03063rem]" viewBox="0 0 31 17" fill="none">
                  <path d="M24.7267 8.63806L30.3864 8.7828L30.3929 7.59186L24.732 7.67072C21.0754 7.65923 18.0794 4.25772 17.8905 0.00746362L16.9009 -1.82991e-05C16.6841 4.25528 13.6632 7.61517 10.0066 7.60368L0.00652463 7.39383L-2.50672e-05 8.5848L10.0013 8.57105C13.6579 8.58254 16.6539 11.9841 16.8428 16.2343L17.8324 16.2418C18.0492 11.9865 21.0702 8.62657 24.7267 8.63806Z" fill="#DADADA"/>
                </svg>
                <div className="flex flex-row items-center gap-[1rem] font-nunito text-[#F2F0EF] text-[1.625rem] font-[300] leading-[100%] tracking-[0.53625rem] uppercase">
                  {glassLabel?.firstText && (
                    <p>{glassLabel?.firstText}</p>
                  )}
                  <span className="w-[4rem] h-[0.125rem] bg-[#F2F0EF]"/>
                  {glassLabel?.secondText && (
                    <p>{glassLabel?.secondText}</p>
                  )}
                </div>
              </div>
            </GlassSurface>

            <div className="flex flex-col gap-[2rem] items-center lg:items-start lg:mt-[4rem] lg:mb-[9rem]">
              <p className="font-nunito text-white text-[1.375rem] lg:text-[1.625rem] font-[300] leading-[100%] tracking-[0.53625rem] uppercase lg:text-start text-center">
                {label}
              </p>

              <div className="w-[min(38.75rem,100%)] lg:w-full text-white prose-headings:font-nunito prose-em:!font-newsreader prose-headings:text-[6rem] lg:prose-headings:text-[9rem] prose-headings:leading-[110%] lg:prose-headings:leading-[90%] prose-headings:uppercase prose-headings:font-[200] prose-headings:mb-0 lg:text-start text-center">
                <PortableText value={title} components={richTextComponent} />
              </div>
            </div>

            <ScrollIndicator className="hidden lg:block" />
          </motion.div>
        </motion.div>

        {/* Gradient Overlay */}
        {useVideo === false && isMobile === false ? (
          <div className="absolute bg-gradient-to-b from-black lg:from-black/95 to-transparent w-full h-[40rem] top-0 left-0 z-[2] pointer-events-none select-none"/>
        ) : (
          <div className="absolute bg-gradient-to-b from-black to-transparent w-full h-[35rem] top-0 left-0 z-[2] pointer-events-none select-none lg:hidden"/>
        )}
        <div className="absolute bg-gradient-to-t from-black lg:from-black/95 to-transparent w-full h-[40rem] bottom-0 left-0 z-[2] pointer-events-none select-none"/>

        <div
          style={{
            background:
              'linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)',
          }}
          className="absolute lg:w-[85rem] w-full opacity-[0.7] h-1/2 lg:h-full left-0 z-[1] pointer-events-none select-none bottom-0 lg:block hidden"
        ></div>

        {useVideo && playbackId && (
          <>
            <div className="absolute inset-0 bottom-0 left-0 max-h-[105rem] justify-center items-center gap-[1.25rem] z-10 lg:hidden flex mix-blend-difference pointer-events-none">
              <button
                type="button"
                aria-label="Open overlay"
                onClick={openOverlay}
                className="pointer-events-auto"
              >
                <div className="py-[3rem] pl-[3.25rem] pr-[2.88rem] border border-white/5 bg-black/[35%] rounded-full backdrop-blur-[1.25rem]">
                  <svg
                    className="w-[2.59813rem] h-auto"
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="21"
                    viewBox="0 0 18 21"
                    fill="none"
                  >
                    <path
                      d="M18 10.3923L-9.08524e-07 20.7846L0 -7.86805e-07L18 10.3923Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </button>
            </div>

            <PlayVideoCursorFollower
              open={open}
              // @ts-ignore
              containerRef={sectionRef}
              label={open ? 'Close Video' : 'Play Video'}
            />

            <button
              type="button"
              aria-label="Open overlay"
              className="absolute inset-0 z-10 pointer-events-none lg:pointer-events-auto"
              onClick={openOverlay}
            />
          </>
        )}

        <motion.div
          className="absolute inset-0 z-0 pointer-events-none select-none"
          style={{ y: isMobile ? 0 : videoY }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 1.2,
            ease: [0.76, 0, 0.24, 1],
          }}
        >
          {useVideo && playbackId ? (
            <VideoAsset
              loadOffset={1000}
              playOffset={300}
              ref={bgRef}
              playbackId={playbackId}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover absolute inset-0"
            />
          ) : (
            <Image
              src={isMobile ? heroBackgroundMobile : heroBackground}
              alt="Hero Background"
              layout="fill"
              objectFit="cover"
              objectPosition="center"
            />
          )}
        </motion.div>

        <AnimatePresence>
          {open && useVideo && playbackId && (
            <motion.div
              key="overlay"
              className="fixed inset-0 z-[60] cursor-default flex items-center justify-center"
              style={{
                willChange: 'clip-path',
                WebkitClipPath: 'inset(50% 50% 50% 50%)',
              }}
              initial={{ clipPath: 'inset(100% 100% 100% 100%)' }}
              animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
              exit={{
                clipPath: 'inset(100% 100% 100% 100%)',
                transition: { duration: 2, ease },
              }}
              transition={{ duration: 1, ease }}
              aria-hidden
            >
              <motion.div
                className="absolute w-full h-full inset-0 overflow-clip bg-black"
                style={{
                  willChange: 'clip-path',
                  WebkitClipPath: 'inset(50% 50% 50% 50%)',
                }}
                initial={{ clipPath: 'inset(100% 100% 100% 100%)' }}
                animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
                exit={{
                  clipPath: 'inset(100% 100% 100% 100%)',
                  transition: { duration: 2, ease, delay: 0.1 },
                }}
                transition={{ duration: 1, ease, delay: 0.1, stiffness: 50 }}
              >
                <VideoControlsMinimal videoRef={modalRef} />

                <button
                  className="lg:hidden block absolute z-[2] top-[3.25rem] right-[3.25rem]"
                  aria-label="Close"
                  onClick={closeOverlay}
                >
                  <svg
                    className="w-[3.2rem] h-[3.2rem]"
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    fill="none"
                    viewBox="0 0 28 28"
                  >
                    <path
                      stroke="#fff"
                      strokeWidth="1.084"
                      d="m1 1 26.062 26.062M1 27.063 27.062 1"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  aria-label="Close"
                  onClick={closeOverlay}
                  className="absolute inset-0 z-[2] cursor-pointer lg:block hidden"
                />

                <div className="lg:w-full lg:h-full">
                  <MuxHLSVideo
                    ref={modalRef}
                    playbackId={playbackId}
                    className="absolute inset-0 w-full h-full lg:object-cover object-contain pointer-events-none video-no-native"
                    autoPlay
                    muted
                    preload="auto"
                    loop
                    playsInline
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="lg:hidden flex flex-col gap-[2rem] items-center justify-end w-full px-[2rem] pb-[4.88rem]">
        <CTAButton className="[&_button]:!min-h-0 [&_button]:!py-[2rem] [&_button]:!bg-transparent [&_button]:!backdrop-blur-0 w-full" data={heroStickyButton?.heroStickyButton2} />
        <CTAButton className="[&_button]:!py-[2rem] [&_button]:!px-[2.5rem] w-full" data={heroStickyButton?.heroStickyButton1} />
      </div>
    </section>
  );
}
